/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { Colors, Typography, Avatar } from '../../../index';
import {
  BackgroundColors,
  remapDeprecatedColors,
  breakpoints,
} from './TestimonialMapping';

export type Props = {
  /**
   * Text content displayed on the top or below the avatar.
   */
  quote: React.ReactNode;
  /**
   * Author name. Text content displayed below quote.
   */
  author: string;
  /**
   * Author avatar url.
   */
  avatarUrl?: string;
  /**
   * Whether or not `TestimonialSolo` has author avatar.
   */
  hasAuthorAvatar?: boolean;
  /**
   * Text content displayed below author initials.
   */
  company: string;
  /**
   * Controls the background of the entire element.
   *
   * Must be one of type `'lava10' | 'ocean10' | 'sun10' | 'white' | 'cream' | 'neutral100' | 'acidWash' | 'neutral200'.
   */
  backgroundColor?: BackgroundColors;
};

const Styles = labelStyles('TestimonialSolo', {
  root: (backgroundColor: Props['backgroundColor'] = 'neutral100') => css`
    background-color: ${Colors[remapDeprecatedColors(backgroundColor)]};
  `,
  content: css`
    padding: 39px 19px;
    margin: 0 auto;
    text-align: center;
    max-width: 500px;

    @media screen and (min-width: ${breakpoints.desktop}px) {
      max-width: 770px;
      padding: 58px 0px;
    }
  `,
  avatarContainer: css`
    margin-bottom: 20px;
  `,
  quote: css`
    ${Typography.paragraph3}
    color: ${Colors.neutral800};
    margin-bottom: 20px;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.subHeader1}
    }
  `,
  author: css`
    ${Typography.smallPrint1Bold}
    color: ${Colors.neutral700};

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.paragraph3Bold}
    }
  `,
  company: css`
    ${Typography.smallPrint1}
    color: ${Colors.neutral700};

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.paragraph3}
    }
  `,
});

export const TestimonialSolo = ({
  backgroundColor,
  avatarUrl,
  hasAuthorAvatar = true,
  quote,
  author,
  company,
}: Props) => {
  return (
    <div css={Styles.root(backgroundColor)}>
      <div css={Styles.content}>
        {hasAuthorAvatar && (
          <div css={Styles.avatarContainer}>
            <Avatar
              data-testId="Avatar"
              name={author}
              url={avatarUrl}
              size={40}
            />
          </div>
        )}
        <div css={Styles.quote}>{quote}</div>
        <p css={Styles.author}>{author}</p>
        <p css={Styles.company}>{company}</p>
      </div>
    </div>
  );
};
